import * as RT from '@radix-ui/react-tooltip';
import { cn } from './cn';

/**
 * <Tooltip content="..." side="top|right|bottom|left">
 *   <button>...</button>
 * </Tooltip>
 *
 * Wrapper minimo sobre @radix-ui/react-tooltip. La idea: ocultar los
 * Provider/Root/Trigger/Portal/Content para que el call site sea 1 linea:
 *
 *   <Tooltip content="Editar total">
 *     <button><Pencil/></button>
 *   </Tooltip>
 *
 * Requiere TooltipProvider montado una vez en App.js para que el delay
 * sea compartido y los tooltips no se peleen.
 *
 * Props:
 *   - content    : string|node   contenido del tooltip (null/undefined => no render)
 *   - side       : 'top'|'right'|'bottom'|'left' (default: top)
 *   - align      : 'start'|'center'|'end' (default: center)
 *   - sideOffset : px de separacion del trigger (default: 6)
 *   - delayDuration : override del delay default (ms)
 *   - disabled   : si true, no envuelve nada (devuelve children pelado)
 *   - className  : extra clase al Content (para casos especiales)
 *   - children   : el elemento que dispara el tooltip (1 hijo, usable con asChild)
 */
const Tooltip = ({
    content,
    side = 'top',
    align = 'center',
    sideOffset = 6,
    delayDuration,
    disabled = false,
    className,
    children
}) => {
    // Sin contenido o disabled => pasar el hijo tal cual.
    if (disabled || content === null || content === undefined || content === '') {
        return children;
    }

    return (
        <RT.Root delayDuration={delayDuration}>
            <RT.Trigger asChild>{children}</RT.Trigger>
            <RT.Portal>
                <RT.Content
                    side={side}
                    align={align}
                    sideOffset={sideOffset}
                    className={cn('tooltip-content', className)}
                >
                    {content}
                    <RT.Arrow className="tooltip-arrow" />
                </RT.Content>
            </RT.Portal>
        </RT.Root>
    );
};

// Provider con valores por defecto razonables. Se monta una vez en App.
export const TooltipProvider = ({ children, delayDuration = 300, skipDelayDuration = 100 }) => (
    <RT.Provider delayDuration={delayDuration} skipDelayDuration={skipDelayDuration}>
        {children}
    </RT.Provider>
);

export default Tooltip;
